import React, { useCallback, useEffect, useMemo, useState, memo } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Button, Row } from "react-bootstrap";
import "../RegisteredUser/CDNPage.css";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import AppToast from "../../AppToast";
import Loader from "../common/Loader";
import { FaX } from "react-icons/fa6";

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 800px)").matches;
const bytesToMB      = (bytes) => bytes / (1024 * 1024);

// ─── Shared button style ──────────────────────────────────────────────────────
const cdnBtnStyle = (extra = {}) => ({
  zIndex: 9, position: "relative",
  fontWeight: 700, color: "white",
  backgroundColor: "#e97730", outline: "4px solid #e97730",
  border: "4px solid white", borderRadius: "30px",
  ...extra,
});

// ─── CName pill (white rounded box with text + optional copy icon) ─────────────
const CNamePill = memo(({ text, onCopy, copied, fontSize = 12, marginLeft = 20 }) => (
  <div style={{ marginTop: 15, display: "flex", alignItems: "center", height: 50, zIndex: 9, position: "relative", backgroundColor: "white", outline: "3px solid #e97730", border: "3px solid white", borderRadius: 30, padding: 5 }}>
    <p style={{ color: "#555", textAlign: "center", fontWeight: 500, fontSize, paddingTop: 15, marginLeft }}>{text}</p>
    {onCopy && (
      <div className="img-wrapper">
        <img className="hover-zoom" src="/images/copy_ic_cdn.svg" style={{ marginLeft: 5, width: 30, height: 30 }} onClick={onCopy} />
        {copied && <span className="blinkStyleCdn">Copied!</span>}
      </div>
    )}
  </div>
));

// ─── Domain validate button or check+verified combo ──────────────────────────
const DomainValidateBtn = memo(({ validated, onValidate, isMobile }) => (
  validated !== 1 ? (
    <button style={cdnBtnStyle({ marginTop: isMobile ? 20 : 15, width: "11rem", height: isMobile ? 40 : 50 })} onClick={onValidate}>
      Domain Validate
    </button>
  ) : (
    <div>
      <img src="/images/verified_success.png" style={{ height: 34, width: 34, zIndex: 9, position: "relative", marginTop: isMobile ? -70 : 15 }} />
      <button style={cdnBtnStyle({ width: "6rem", height: 40, position: "relative", left: isMobile ? "2%" : "45%", bottom: 35 })} onClick={onValidate}>
        Check
      </button>
    </div>
  )
));

// ─── Unified CDN card row (Active) — shared between mobile + web ──────────────
const ActiveCDNCard = memo(({
  item, idx, isMobile, hoveredIndex, setHoveredIndex,
  copiedStatus1, copiedStatus2, copiedStatus3, copiedStatus4,
  onCopy1, onCopy2, onCopy3, onCopy4,
  onDisable, onValidateDomain, onARNValidate,
  onUsedCDN, onClearCache, onGetCname, onRecharge,
}) => {
  const fontSize = isMobile ? 15 : 20;

  return (
    <div className="table-row-noti">
      <div className="bar" />
      <div className="message" style={isMobile ? { margin: "5px 0" } : undefined}>
        {/* Info fields */}
        <Row>
          <div className={`col-md-3`} style={isMobile ? undefined : { marginLeft: 10 }}>
            <div className="desc" style={{ fontWeight: 500, fontSize, position: isMobile ? "relative" : undefined }}>
              Person Name: {item.person_name}
              {isMobile && (
                <button style={{ ...cdnBtnStyle({ width: "7rem", height: 40, fontSize: 15 }), position: "relative", marginTop: 25, right: -60, transform: "translateY(-50%)", backgroundColor: hoveredIndex === idx ? "#b71b1b" : "#e97730", outline: hoveredIndex === idx ? "4px solid #b71b1b" : "4px solid #e97730" }}
                  onClick={() => hoveredIndex === idx ? onDisable() : undefined}
                  onMouseEnter={() => setHoveredIndex(idx)} onMouseLeave={() => setHoveredIndex(null)}>
                  {hoveredIndex === idx ? "Disable" : "Active"}
                </button>
              )}
            </div>
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Website URL : {item.website_url}</div>
          </div>
          <div className="col-md-2">
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Contact Number: {item.contact_number}</div>
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Bandwidth: {item.datatransfer_value} TB</div>
          </div>
          <div className="col-md-6">
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Company Name: {item.company_name}</div>
          </div>
          {isMobile && (
            <div className="datetime" style={{ maxHeight: "none", marginTop: 10, fontSize }}>
              Created: {item.created_at}
            </div>
          )}
        </Row>

        {/* CNAME Record 1 */}
        <div style={{ marginTop: 15, marginLeft: isMobile ? 0 : 10, color: "#035189", fontWeight: 500, fontSize: isMobile ? 16 : 18 }}>
          ADD CNAME Record:
        </div>
        <Row>
          <div className="col-md-5">
            <CNamePill
              text="WWW"
              onCopy={item.cdn_name ? () => onCopy1("WWW", idx) : undefined}
              copied={copiedStatus1[idx]}
              fontSize={isMobile ? 12 : 15}
            />
          </div>
          <div className="col-md-5">
            <CNamePill
              text={item.cdn_name}
              onCopy={item.cdn_name ? () => onCopy2(item.cdn_name, idx) : undefined}
              copied={copiedStatus2[idx]}
              fontSize={isMobile ? 10 : 15}
            />
          </div>
          <div className="col-md-1">
            <DomainValidateBtn validated={item.domain_validate} onValidate={onValidateDomain} isMobile={isMobile} />
          </div>
        </Row>

        {/* CNAME Record 2 (conditional) */}
        {item.cname_name && item.cname_value && (
          <>
            <div style={{ marginTop: 15, marginLeft: isMobile ? 0 : 10, color: "#035189", fontWeight: 500, fontSize: isMobile ? 16 : 18 }}>
              ADD CNAME Record:
            </div>
            <Row>
              <div className="col-md-5">
                <CNamePill
                  text={item.cname_name}
                  onCopy={() => { navigator.clipboard.writeText(item.cname_name); onCopy3(item.cname_name, idx); }}
                  copied={copiedStatus3[idx]}
                  fontSize={isMobile ? 9 : 15}
                />
              </div>
              <div className="col-md-5">
                <CNamePill
                  text={item.cname_value}
                  onCopy={() => onCopy4(item.cname_value, idx)}
                  copied={copiedStatus4[idx]}
                  fontSize={isMobile ? 8 : 14}
                />
              </div>
              <div className="col-md-1">
                <button style={cdnBtnStyle({ width: "11rem", marginTop: isMobile ? 20 : 15, height: isMobile ? 40 : 50 })} onClick={onARNValidate}>
                  Certificate Validate
                </button>
              </div>
            </Row>
          </>
        )}

        {/* Action buttons */}
        <div style={{ marginTop: 5 }}>
          <button style={cdnBtnStyle({ width: isMobile ? "6rem" : "10rem", height: isMobile ? 40 : 50, marginTop: isMobile ? 15 : 30, marginBottom: 10 })} onClick={onUsedCDN}>
            CDN Used
          </button>
          <button style={cdnBtnStyle({ width: isMobile ? "7rem" : "10rem", height: isMobile ? 40 : 50, marginLeft: 15, marginTop: 20 })} onClick={onClearCache}>
            Clear Cache
          </button>
          {!item.cname_name && !item.cname_value && item.domain_validate === 1 && (
            <button className="loader" style={cdnBtnStyle({ width: "10rem", height: isMobile ? 40 : 50, marginLeft: isMobile ? 0 : 20, marginTop: 15 })} onClick={onGetCname}>
              Generate CName
            </button>
          )}
          <button style={cdnBtnStyle({ width: isMobile ? "6rem" : "10rem", height: isMobile ? 40 : 50, marginLeft: 15, marginTop: 20 })} onClick={onRecharge}>
            Recharge
          </button>
        </div>
      </div>

      {/* Disable / Active toggle (web only — mobile has it inline above) */}
      {!isMobile && (
        <div>
          <button style={{ ...cdnBtnStyle({ width: "10rem", height: 50, marginTop: 15 }), backgroundColor: hoveredIndex === idx ? "#b71b1b" : "#e97730", outline: hoveredIndex === idx ? "4px solid #b71b1b" : "4px solid #e97730", border: "4px solid white" }}
            onClick={() => hoveredIndex === idx ? onDisable() : undefined}
            onMouseEnter={() => setHoveredIndex(idx)} onMouseLeave={() => setHoveredIndex(null)}>
            {hoveredIndex === idx ? "Disable" : "Active"}
          </button>
          <p style={{ fontWeight: 600, marginTop: 100 }}>Created</p>
          <p style={{ position: "relative", marginTop: -40 }}>{item.created_at}</p>
        </div>
      )}
    </div>
  );
});

// ─── Disabled CDN card — shared between mobile + web ─────────────────────────
const DisabledCDNCard = memo(({ item, isMobile }) => {
  const fontSize = isMobile ? 15 : 20;
  return (
    <div className="table-row-noti">
      <div className="bar" />
      <div className="message">
        <Row>
          <div className={`col-md-3`} style={isMobile ? undefined : { marginLeft: 10 }}>
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Person Name: {item.person_name}</div>
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Website URL : {item.website_url}</div>
          </div>
          <div className="col-md-2">
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Contact Number: {item.contact_number}</div>
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Bandwidth: {item.datatransfer_value} TB</div>
          </div>
          <div className="col-md-6">
            <div className="desc" style={{ fontWeight: 500, fontSize }}>Company Name: {item.company_name}</div>
          </div>
          {isMobile && <div className="datetime" style={{ maxHeight: "none", marginTop: 10, fontSize }}>Created: {item.created_at}</div>}
        </Row>

        <div style={{ marginTop: 15, color: "#035189", fontWeight: 500, fontSize: isMobile ? 16 : 18 }}>ADD CNAME Record:</div>
        <Row>
          <div className="col-md-5"><CNamePill text="WWW"           fontSize={isMobile ? 12 : 18} /></div>
          <div className={isMobile ? "col-md-6" : "col-md-6"}><CNamePill text={item.cdn_name} fontSize={isMobile ? 10 : 18} /></div>
        </Row>

        {item.cname_name && item.cname_value && (
          <>
            <div style={{ marginTop: 15, color: "#035189", fontWeight: 500, fontSize: isMobile ? 16 : 18 }}>ADD CNAME Record:</div>
            <Row>
              <div className="col-md-5"><CNamePill text={item.cname_name}  fontSize={isMobile ? 10 : 18} /></div>
              <div className="col-md-6"><CNamePill text={item.cname_value} fontSize={isMobile ? 10 : 18} /></div>
            </Row>
          </>
        )}

        {!isMobile && (
          <div className="datetime">
            <p style={{ fontWeight: 600 }}>Created</p>
            <p style={{ position: "relative", marginTop: -40 }}>{item.created_at}</p>
          </div>
        )}
      </div>
    </div>
  );
});

// ─── Search bar ───────────────────────────────────────────────────────────────
const SearchBar = memo(({ value, onChange, isMobile }) => (
  <div className="input-container" style={{ marginLeft: isMobile ? "52%" : "82%", position: "relative", border: "2px solid #035189", width: isMobile ? "12rem" : "18rem", marginTop: isMobile ? "70px" : "-3rem", height: isMobile ? 40 : 55 }}>
    <input type="text" name="search" className="input-signup input-tickets" placeholder="Search Website" value={value}
      style={{ fontSize: 24, color: "black", textAlign: "center", width: 10 }} onChange={(e) => onChange(e.target.value)} />
    {value && (
      <button style={{ backgroundColor: "transparent", border: "none" }} onClick={() => onChange("")}>
        <FaX style={{ color: "#154e7a", fontSize: 19 }} />
      </button>
    )}
  </div>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const AssignedCDNPage = () => {
  const navigate          = useNavigate();
  const location          = useLocation();
  const { smuser }        = useAuth();
  const selectedUser      = location.state?.userDetail ?? null;

  const [isMobile, setIsMobile]               = useState(isMobileDevice);
  const [loading, setLoading]                 = useState(false);
  const [cdnList, setCdnList]                 = useState(null);
  const [searchText, setSearchText]           = useState("");
  const [activeStatusButton, setActiveStatusButton] = useState("Active");
  const [hoveredIndex, setHoveredIndex]       = useState(null);
  const [isCDNUsedView, setIsCDNUsedView]     = useState(false);
  const [totalBytesDownloaded, setTotalBytesDownloaded] = useState("");
  const [websiteURL, setWebsiteURL]           = useState("");

  // 4 independent copied-status arrays (one per copy target)
  const [copiedStatus1, setCopiedStatus1] = useState([]);
  const [copiedStatus2, setCopiedStatus2] = useState([]);
  const [copiedStatus3, setCopiedStatus3] = useState([]);
  const [copiedStatus4, setCopiedStatus4] = useState([]);

  // ── Toast helper ─────────────────────────────────────────────────────────
  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />),
    [isMobile]);

  // ── Generic API call helper ───────────────────────────────────────────────
  const apiCall = useCallback(async (url, payload, onSuccess) => {
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest(payload);
      const res  = await instance.post(url, enc);
      const data = await decryptData(res.data);
      if (onSuccess) onSuccess(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  // ── List CDN ──────────────────────────────────────────────────────────────
  const ListCDN = useCallback(async () => {
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: selectedUser.id });
      const res  = await instance.post("/childcdnlist", enc);
      const data = await decryptData(res.data);
      const list = data.cdn_list.reverse();
      setCdnList(list);
      const blank = Array(list.length).fill(false);
      setCopiedStatus1([...blank]);
      setCopiedStatus2([...blank]);
      setCopiedStatus3([...blank]);
      setCopiedStatus4([...blank]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedUser?.id]);

  // ── Mount
  // Fix: original had [isMobile] as dep — caused ListCDN + CdnPrice to re-fire
  // on every window resize. Changed to [] — runs once only.
  useEffect(() => {
    window.scrollTo(0, 0);
    ListCDN();
    // CdnPrice not used in render — skipped
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Copy handlers — consolidated from 4 identical functions ───────────────
  const makeCopyHandler = useCallback((setStatus, getStatus) => (text, idx) => {
    setStatus((prev) => { const n = [...prev]; n[idx] = true; return n; });
    navigator.clipboard.writeText(text).then(() => {
      setTimeout(() => setStatus((prev) => { const n = [...prev]; n[idx] = false; return n; }), 2000);
    });
  }, []);

  const handleCopy1 = useMemo(() => makeCopyHandler(setCopiedStatus1), [makeCopyHandler]);
  const handleCopy2 = useMemo(() => makeCopyHandler(setCopiedStatus2), [makeCopyHandler]);
  const handleCopy3 = useMemo(() => makeCopyHandler(setCopiedStatus3), [makeCopyHandler]);
  const handleCopy4 = useMemo(() => makeCopyHandler(setCopiedStatus4), [makeCopyHandler]);

  // ── CDN actions ───────────────────────────────────────────────────────────
  const UsedCDN = useCallback((pull_zone_id, websiteUrl, Id) =>
    apiCall("/usedcdn", { user_id: smuser.id, distribution_id: pull_zone_id, id: Id }, (data) => {
      if (data.status) {
        setIsCDNUsedView(true);
        setTotalBytesDownloaded(bytesToMB(data.totalBytesDownloaded));
        setWebsiteURL(websiteUrl);
      }
    }), [smuser?.id, apiCall]);

  const createInvalidation = useCallback((pull_zone_id) =>
    apiCall("/createInvalidation", { user_id: smuser.id, distribution_id: pull_zone_id }), [smuser?.id, apiCall]);

  const getCname = useCallback((pull_zone_id, arnVal) =>
    apiCall("/getcname", { user_id: smuser.id, distribution_id: pull_zone_id, arn: arnVal }, () => ListCDN()), [smuser?.id, apiCall, ListCDN]);

  const ARNvalidate = useCallback((pull_zone_id, domain_name) =>
    apiCall("/arnvalidate", { user_id: smuser.id, distribution_id: pull_zone_id, domain_name }, (data) => {
      ListCDN();
      if (data.message) showToast(`${data.DomainName}\n ip: ${data.message}`);
    }), [smuser?.id, apiCall, ListCDN, showToast]);

  const validateDomain = useCallback((pull_zone_id, domain_name, arnVal) =>
    apiCall("/validate_domain", { user_id: smuser.id, distribution_id: pull_zone_id, domain_name, arn: arnVal }, (data) => {
      ListCDN();
      if (data.message) showToast(data.message);
    }), [smuser?.id, apiCall, ListCDN, showToast]);

  const disableCDN = useCallback((pull_zone_id) =>
    apiCall("/disable", { user_id: smuser.id, distribution_id: pull_zone_id }, (data) => {
      if (data.message) { showToast(data.message); setActiveStatusButton("Disabled"); }
      ListCDN();
    }), [smuser?.id, apiCall, ListCDN, showToast]);

  const handleRedirect = useCallback(() =>
    navigate("/wallet", { state: { isServerOrCDN: "CDN" } }), [navigate]);

  // ── Filtered lists (memoized) ─────────────────────────────────────────────
  const filteredActive = useMemo(() =>
    cdnList?.filter((i) => i.status === 1 && i.website_url.toLowerCase().includes(searchText.toLowerCase())) ?? [],
    [cdnList, searchText]);

  const filteredDisabled = useMemo(() =>
    cdnList?.filter((i) => i.status === 0 && i.website_url.toLowerCase().includes(searchText.toLowerCase())) ?? [],
    [cdnList, searchText]);

  // ── Shared card props ─────────────────────────────────────────────────────
  const sharedActiveProps = { hoveredIndex, setHoveredIndex, copiedStatus1, copiedStatus2, copiedStatus3, copiedStatus4, onCopy1: handleCopy1, onCopy2: handleCopy2, onCopy3: handleCopy3, onCopy4: handleCopy4 };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", minHeight: "65rem", position: "relative", backgroundImage: "url(/main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>
      {loading && <div className="loading-overlay" style={{ zIndex: 999999999 }}><Loader isLoading={loading} /></div>}

      {/* CDN Used popup */}
      {isCDNUsedView && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backgroundColor: "#035189", borderRadius: 12, border: "2px solid #e97730", top: "30%", position: "absolute", zIndex: 999999, width: isMobile ? "100%" : "20%", height: isMobile ? "25%" : "15rem" }}>
            <div style={{ display: "grid", justifyItems: "center" }}>
              <button style={{ zIndex: 9, position: "absolute", backgroundColor: "transparent", border: "none", right: 0 }} onClick={() => setIsCDNUsedView(false)}>
                <FaX style={{ marginTop: 5, color: "#e97730", fontSize: 19 }} />
              </button>
              <h1 style={{ color: "white", width: "25rem", fontSize: 22, fontWeight: "bold", marginTop: "4rem", textAlign: "center" }}>
                Website URL : {websiteURL}<br /><br />
                Total CDN Used : {totalBytesDownloaded} MB
              </h1>
            </div>
          </div>
        </div>
      )}

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────── */
        <div style={{ height: "50rem" }}>
          <div className="heading-dotted-cdn">CDNs <span /></div>
          <SearchBar value={searchText} onChange={setSearchText} isMobile />

          {activeStatusButton === "Active" && (
            <div style={{ marginTop: "6rem", marginLeft: 10, width: "95%", maxHeight: "50rem", overflowY: "auto" }}>
              {filteredActive.map((item, idx) => (
                <ActiveCDNCard key={item.id ?? idx} item={item} idx={idx} isMobile
                  {...sharedActiveProps}
                  onDisable={() => disableCDN(item.pull_zone_id)}
                  onValidateDomain={() => validateDomain(item.pull_zone_id, item.website_url, item.arn)}
                  onARNValidate={() => ARNvalidate(item.pull_zone_id, item.website_url)}
                  onUsedCDN={() => UsedCDN(item.pull_zone_id, item.website_url, item.id)}
                  onClearCache={() => createInvalidation(item.pull_zone_id)}
                  onGetCname={() => getCname(item.pull_zone_id, item.arn)}
                  onRecharge={handleRedirect}
                />
              ))}
            </div>
          )}

          {activeStatusButton === "Disabled" && (
            <div style={{ marginTop: "5rem", marginLeft: 10, width: "95%", maxHeight: "50rem", overflowY: "auto" }}>
              {filteredDisabled.map((item, idx) => (
                <DisabledCDNCard key={item.id ?? idx} item={item} isMobile />
              ))}
              {filteredDisabled.length === 0 && <div style={{ textAlign: "center", fontSize: 25, marginTop: 20 }}>No Records</div>}
            </div>
          )}
        </div>
      ) : (
        /* ── WEB ─────────────────────────────────────────────────────────── */
        <div className="features-page-solution" style={{ padding: "5rem" }}>
          <div className="heading-dotted-cdn">CDNs <span /></div>
          {selectedUser && (
            <Button style={{ marginLeft: 20, background: "#035189", border: "none", fontSize: 20, padding: "5px 15px", color: "#fff", fontWeight: 400, marginBottom: 10 }}>
              {selectedUser.name}, {selectedUser.email}
            </Button>
          )}
          <SearchBar value={searchText} onChange={setSearchText} isMobile={false} />

          {activeStatusButton === "Active" && (
            <div style={{ marginTop: "2rem", marginLeft: "8rem" }}>
              {filteredActive.map((item, idx) => (
                <ActiveCDNCard key={item.id ?? idx} item={item} idx={idx} isMobile={false}
                  {...sharedActiveProps}
                  onDisable={() => disableCDN(item.pull_zone_id)}
                  onValidateDomain={() => validateDomain(item.pull_zone_id, item.website_url, item.arn)}
                  onARNValidate={() => ARNvalidate(item.pull_zone_id, item.website_url)}
                  onUsedCDN={() => UsedCDN(item.pull_zone_id, item.website_url, item.id)}
                  onClearCache={() => createInvalidation(item.pull_zone_id)}
                  onGetCname={() => getCname(item.pull_zone_id, item.arn)}
                  onRecharge={handleRedirect}
                />
              ))}
            </div>
          )}

          {activeStatusButton === "Disabled" && (
            <div style={{ marginTop: "2rem", marginLeft: "8rem" }}>
              {filteredDisabled.map((item, idx) => (
                <DisabledCDNCard key={item.id ?? idx} item={item} isMobile={false} />
              ))}
              {filteredDisabled.length === 0 && <div style={{ textAlign: "center", fontSize: 25, marginTop: 20 }}>No Records</div>}
            </div>
          )}
        </div>
      )}

      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>
    </div>
  );
};

export default AssignedCDNPage;